import { useSyncExternalStore } from "react";
import { formatDistanceToNow, format } from "date-fns";

export type CurrencyCode =
  | "USD"
  | "AED"
  | "BGN"
  | "BRL"
  | "CAD"
  | "CHF"
  | "CNY"
  | "CZK"
  | "DKK"
  | "EUR"
  | "GBP"
  | "HKD"
  | "HUF"
  | "ILS"
  | "INR"
  | "JPY"
  | "MXN"
  | "NOK"
  | "NZD"
  | "PLN"
  | "RON"
  | "SEK"
  | "SGD"
  | "TRY"
  | "ZAR"
  | "AUD";
export type CurrencyPlacement = "before" | "after" | "none";
export type NumberFormat = "us" | "eu";
export type DateFormat = "mmm_d_yyyy" | "yyyy_mm_dd" | "dd_mm_yyyy" | "mm_dd_yyyy" | "d_mmm_yyyy";

export type FormattingSettings = {
  currency: CurrencyCode;
  currencyPlacement: CurrencyPlacement;
  numberFormat: NumberFormat;
  dateFormat: DateFormat;
  hideAmounts: boolean;
};

const DEFAULT_FORMATTING_SETTINGS: FormattingSettings = {
  currency: "USD",
  currencyPlacement: "before",
  numberFormat: "us",
  dateFormat: "mmm_d_yyyy",
  hideAmounts: false,
};

const FORMATTING_STORAGE_KEY = "fintective.dashboard.formatting.v1";
const FORMATTING_UPDATED_EVENT = "fintective:formatting-updated";
export const MASKED_AMOUNT = "*****";
const SUPPORTED_CURRENCIES: CurrencyCode[] = [
  "USD",
  "AED",
  "AUD",
  "BGN",
  "BRL",
  "CAD",
  "CHF",
  "CNY",
  "CZK",
  "DKK",
  "EUR",
  "GBP",
  "HKD",
  "HUF",
  "ILS",
  "INR",
  "JPY",
  "MXN",
  "NOK",
  "NZD",
  "PLN",
  "RON",
  "SEK",
  "SGD",
  "TRY",
  "ZAR",
];

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function coerceSettings(value: unknown): FormattingSettings {
  if (!isObject(value)) {
    return DEFAULT_FORMATTING_SETTINGS;
  }

  const currency = value.currency;
  const currencyPlacement = value.currencyPlacement;
  const numberFormat = value.numberFormat;
  const dateFormat = value.dateFormat;
  const hideAmounts = value.hideAmounts;

  return {
    currency: typeof currency === "string" && SUPPORTED_CURRENCIES.includes(currency as CurrencyCode) ? (currency as CurrencyCode) : "USD",
    currencyPlacement: currencyPlacement === "after" || currencyPlacement === "none" ? currencyPlacement : "before",
    numberFormat: numberFormat === "eu" ? "eu" : "us",
    dateFormat:
      dateFormat === "yyyy_mm_dd" ||
      dateFormat === "dd_mm_yyyy" ||
      dateFormat === "mm_dd_yyyy" ||
      dateFormat === "d_mmm_yyyy"
        ? dateFormat
        : "mmm_d_yyyy",
    hideAmounts: hideAmounts === true,
  };
}

let _cachedRaw: string | null = null;
let _cachedSettings: FormattingSettings = DEFAULT_FORMATTING_SETTINGS;

export function getFormattingSettings(): FormattingSettings {
  if (typeof window === "undefined") {
    return DEFAULT_FORMATTING_SETTINGS;
  }

  try {
    const raw = window.localStorage.getItem(FORMATTING_STORAGE_KEY);
    if (raw === _cachedRaw) return _cachedSettings;
    _cachedRaw = raw;
    _cachedSettings = raw ? coerceSettings(JSON.parse(raw)) : DEFAULT_FORMATTING_SETTINGS;
    return _cachedSettings;
  } catch {
    return DEFAULT_FORMATTING_SETTINGS;
  }
}

export function saveFormattingSettings(settings: FormattingSettings): void {
  if (typeof window === "undefined") {
    return;
  }

  const coerced = coerceSettings(settings);
  window.localStorage.setItem(FORMATTING_STORAGE_KEY, JSON.stringify(coerced));
  window.dispatchEvent(new Event(FORMATTING_UPDATED_EVENT));
}

// clearFormattingSettings resets the cached formatting preferences to the hardcoded
// defaults. Call on sign-out so the next account on this browser doesn't inherit the
// previous agency's preferences before its own fetch resolves.
export function clearFormattingSettings(): void {
  if (typeof window === "undefined") {
    return;
  }

  window.localStorage.removeItem(FORMATTING_STORAGE_KEY);
  _cachedRaw = null;
  _cachedSettings = DEFAULT_FORMATTING_SETTINGS;
  window.dispatchEvent(new Event(FORMATTING_UPDATED_EVENT));
}

export function subscribeFormattingSettings(onChange: () => void): () => void {
  if (typeof window === "undefined") {
    return () => undefined;
  }

  const handleStorage = (event: StorageEvent) => {
    if (event.key === FORMATTING_STORAGE_KEY) {
      onChange();
    }
  };
  const handleCustom = () => onChange();

  window.addEventListener("storage", handleStorage);
  window.addEventListener(FORMATTING_UPDATED_EVENT, handleCustom);

  return () => {
    window.removeEventListener("storage", handleStorage);
    window.removeEventListener(FORMATTING_UPDATED_EVENT, handleCustom);
  };
}

export function useFormattingSettings(): FormattingSettings {
  return useSyncExternalStore(
    subscribeFormattingSettings,
    getFormattingSettings,
    () => DEFAULT_FORMATTING_SETTINGS,
  );
}

// formatCurrencyMajor formats a major-unit amount (e.g. dollars, not milli-units)
// using the agency's selected currency, placement, and number format.
export function formatCurrencyMajor(value: number): string {
  const settings = getFormattingSettings();
  if (settings.hideAmounts) {
    return MASKED_AMOUNT;
  }
  const locale = settings.numberFormat === "eu" ? "de-DE" : "en-US";
  const options: Intl.NumberFormatOptions = {
    style: "currency",
    currency: settings.currency,
    currencyDisplay: "symbol",
    minimumFractionDigits: 2,
  };

  if (settings.currencyPlacement === "before") {
    return new Intl.NumberFormat(locale, options).format(value);
  }

  const parts = new Intl.NumberFormat(locale, options).formatToParts(value);
  const amountText = parts
    .filter((part) => part.type !== "currency" && part.type !== "literal")
    .map((part) => part.value)
    .join("");
  const currencyText = parts.find((part) => part.type === "currency")?.value || settings.currency;

  if (settings.currencyPlacement === "none") {
    return amountText;
  }

  return `${amountText} ${currencyText}`;
}

/** Formats an amount (milli-units) in a given currency, e.g. the original of a converted transaction. */
export function formatMilliUnitsIn(amount: number, currency: string): string {
  const settings = getFormattingSettings();
  if (settings.hideAmounts) {
    return MASKED_AMOUNT;
  }
  const locale = settings.numberFormat === "eu" ? "de-DE" : "en-US";
  try {
    return new Intl.NumberFormat(locale, { style: "currency", currency, currencyDisplay: "symbol", minimumFractionDigits: 2 }).format(amount / 1000);
  } catch {
    return `${(amount / 1000).toFixed(2)} ${currency}`;
  }
}

export function formatMilliUnits(amount: number): string {
  return formatCurrencyMajor(amount / 1000);
}

export function formatNumber(value: number): string {
  const settings = getFormattingSettings();
  const locale = settings.numberFormat === "eu" ? "de-DE" : "en-US";
  return new Intl.NumberFormat(locale).format(value);
}

export function formatRelativeDate(dateStr: string): string {
  return formatDistanceToNow(new Date(dateStr), { addSuffix: true });
}

export function formatDate(dateStr: string): string {
  const settings = getFormattingSettings();

  if (settings.dateFormat === "yyyy_mm_dd") {
    return format(new Date(dateStr), "yyyy-MM-dd");
  }

  if (settings.dateFormat === "dd_mm_yyyy") {
    return format(new Date(dateStr), "dd/MM/yyyy");
  }

  if (settings.dateFormat === "mm_dd_yyyy") {
    return format(new Date(dateStr), "MM/dd/yyyy");
  }

  if (settings.dateFormat === "d_mmm_yyyy") {
    return format(new Date(dateStr), "d MMM, yyyy");
  }

  return format(new Date(dateStr), "MMM d, yyyy");
}

export function formatDateTime(dateStr: string): string {
  const settings = getFormattingSettings();
  const locale = settings.numberFormat === "eu" ? "de-DE" : "en-US";
  const value = new Date(dateStr);

  if (settings.dateFormat === "yyyy_mm_dd") {
    return `${format(value, "yyyy-MM-dd")} ${value.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" })}`;
  }

  if (settings.dateFormat === "dd_mm_yyyy") {
    return `${format(value, "dd/MM/yyyy")} ${value.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" })}`;
  }

  if (settings.dateFormat === "mm_dd_yyyy") {
    return `${format(value, "MM/dd/yyyy")} ${value.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" })}`;
  }

  if (settings.dateFormat === "d_mmm_yyyy") {
    return `${format(value, "d MMM, yyyy")} ${value.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" })}`;
  }

  return `${format(value, "MMM d, yyyy")} ${value.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" })}`;
}

export function maskEmail(email: string): string {
  const [local, domain] = email.split("@");
  return `${local[0]}***@${domain}`;
}
